import React, { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, Space, Tooltip, Typography, theme } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { PiSparkleFill } from 'react-icons/pi';
import { Link } from 'react-router-dom';

import SupportService from 'services/SupportService';

import AiSupportChat from './AiSupportChat';

const { Text } = Typography;

/*
| The assistant, reachable from anywhere in the app.
|
| ── Why the header, and not a floating bubble ──────────────────────────────────
|
| The bottom-right bubble is the convention, and it is the wrong convention here.
|
|   1. That corner is taken. DashboardLayout pins a FooterPanelTriggerBar across the bottom
|      of every screen, and a bubble would sit on top of it.
|   2. This is an ERP. The bottom-right of a dense screen is where pagination, totals and
|      table action bars live — the bubble would cover the numbers someone is asking about.
|   3. A bubble reads as a third-party widget bolted on. The header reads as part of the
|      product, which is what it is.
|
| So it lives in the header's right-hand cluster, beside Todos and the profile menu — where
| persistent tools already live, and where people look for them. The centre of the header is
| for create actions (New Invoice, New Estimate, New Customer); help is not a create action.
|
| ── Why a drawer, and not a modal ──────────────────────────────────────────────
|
| A modal would cover the screen the question is about. Someone asking "why is this invoice
| still unpaid" needs to see the invoice while they ask. The right-hand drawer leaves the
| content visible and is the pattern this app already uses for contextual panels.
*/

const DRAWER_WIDTH = 460;

/** Mac shows ⌘K, everything else Ctrl K. Detected once, cosmetically. */
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '');
const SHORTCUT_LABEL = isMac ? '⌘K' : 'Ctrl K';

const AiAssistantLauncher = () => {
    const { token } = theme.useToken();

    const [available, setAvailable] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let active = true;

        SupportService.isAvailable().then((enabled) => {
            if (active) setAvailable(enabled);
        });

        return () => {
            active = false;
        };
    }, []);

    /*
      Cmd/Ctrl+K opens it from anywhere.

      Suppressed while the focus is in a field, because the same chord is a text shortcut in
      a browser and stealing it mid-sentence is worse than not having a shortcut at all.
    */
    const onKeyDown = useCallback(
        (event) => {
            if (!available) return;
            if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;

            const el = document.activeElement;
            const typing =
                el &&
                (el.tagName === 'INPUT' ||
                    el.tagName === 'TEXTAREA' ||
                    el.isContentEditable);

            if (typing) return;

            event.preventDefault();
            setOpen((wasOpen) => !wasOpen);
        },
        [available],
    );

    useEffect(() => {
        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onKeyDown]);

    // Nothing rendered when the assistant is switched off — a button that always fails is
    // worse than no button. The support page is unaffected and still works.
    if (!available) return null;

    return (
        <>
            <Tooltip
                title={
                    <Space size={6}>
                        <span>Ask Gridbind</span>
                        <Text
                            keyboard
                            style={{ color: 'inherit', fontSize: token.fontSizeSM, opacity: 0.75 }}
                        >
                            {SHORTCUT_LABEL}
                        </Text>
                    </Space>
                }
            >
                <Button
                    className="m-0 p-0"
                    color="default"
                    variant="filled"
                    aria-label="Ask Gridbind"
                    icon={<PiSparkleFill className="fs-1" style={{ color: token.colorPrimary }} />}
                    onClick={() => setOpen(true)}
                />
            </Tooltip>

            <Drawer
                title={
                    <Space size={10} align="center">
                        <span
                            aria-hidden
                            style={{
                                display: 'grid',
                                placeItems: 'center',
                                width: 30,
                                height: 30,
                                fontSize: 15,
                                borderRadius: token.borderRadius,
                                color: token.colorPrimary,
                                background: token.colorPrimaryBg,
                            }}
                        >
                            <PiSparkleFill />
                        </span>
                        <span>Ask Gridbind</span>
                    </Space>
                }
                placement="right"
                size={DRAWER_WIDTH}
                onClose={() => setOpen(false)}
                open={open}
                closable
                keyboard
                /*
                  destroyOnHidden is deliberate: closing the drawer ends the conversation.

                  A support chat that silently resumes a thread from two screens ago answers
                  the wrong question — the assistant would still be reasoning about invoices
                  while the person is now asking about payroll. Each open starts clean, which
                  is also what someone reaching for help on a new screen expects.
                */
                destroyOnHidden
                styles={{ body: { display: 'flex', flexDirection: 'column', paddingTop: token.padding } }}
                footer={
                    <Link to="/support" onClick={() => setOpen(false)}>
                        Browse all help topics <ArrowRightOutlined className="fs--2" />
                    </Link>
                }
            >
                <AiSupportChat fill />
            </Drawer>
        </>
    );
};

export default AiAssistantLauncher;
